import React, { useState } from "react";
import { Plus, Trash2, Baby, User } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Add family members (mark children to enable their Treasure Chest).
export function FamilyManager({ family, onChange }) {
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [isChild, setIsChild] = useState(false);
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/portal/family", { name, relationship, is_child: isChild });
      setName(""); setRelationship(""); setIsChild(false);
      toast.success("Family member added");
      onChange && onChange();
    } catch {
      toast.error("Could not add member");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (mid) => {
    try {
      await api.delete(`/portal/family/${mid}`);
      toast.success("Removed");
      onChange && onChange();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div data-testid="family-manager" className="bg-ivory border border-espresso/10 rounded-2xl p-8">
      <div className="overline mb-2">Family Profiles</div>
      <h3 className="font-serif text-2xl text-espresso mb-6">Everyone in your care</h3>

      <div className="space-y-3 mb-8">
        {(family || []).map((m) => (
          <div key={m.id} data-testid={`family-member-${m.id}`} className="flex items-center gap-4 bg-linen rounded-xl px-5 py-4">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-espresso/5 shrink-0">
              {m.is_child ? <Baby size={18} className="text-brass" /> : <User size={18} className="text-taupe" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg text-espresso truncate">{m.name}</div>
              <div className="text-xs uppercase tracking-[0.14em] text-taupe">{m.relationship || (m.is_child ? "Child" : "Family")}</div>
            </div>
            <button data-testid={`family-remove-${m.id}`} onClick={() => remove(m.id)} className="text-destructive/60 hover:text-destructive"><Trash2 size={17} /></button>
          </div>
        ))}
        {(family || []).length === 0 && <p className="text-taupe text-sm">No family members yet — add your children to start their Treasure Chest.</p>}
      </div>

      <form onSubmit={add} className="grid sm:grid-cols-2 gap-3">
        <input data-testid="family-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="bg-ivory border border-espresso/20 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brass" />
        <input data-testid="family-relationship" value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="Relationship (e.g. Son)" className="bg-ivory border border-espresso/20 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brass" />
        <label className="flex items-center gap-3 text-sm text-espresso sm:col-span-2 cursor-pointer">
          <input data-testid="family-is-child" type="checkbox" checked={isChild} onChange={(e) => setIsChild(e.target.checked)} className="h-4 w-4 accent-brass" />
          This is a child (enables their Treasure Chest)
        </label>
        <button data-testid="family-add" type="submit" disabled={busy} className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-espresso text-ivory rounded-full py-3 text-xs uppercase tracking-[0.18em] hover:bg-brass transition-colors disabled:opacity-60">
          <Plus size={15} /> Add Family Member
        </button>
      </form>
    </div>
  );
}
